import { useCallback, useEffect, useMemo, useState } from 'react';
import type { PublicResearchModel } from '@/lib/research-assistant/models';
import type { ResearchExecutionMode } from '@/lib/research-assistant/runtime/run-controller';

const STORAGE_KEY = 'attune.research.selectedModelId.v1';

interface UseResearchModelSelectionParams {
  apiCall: (body: Record<string, unknown>, init?: RequestInit) => Promise<Response>;
}

export function useResearchModelSelection({ apiCall }: UseResearchModelSelectionParams) {
  const [models, setModels] = useState<PublicResearchModel[]>([]);
  const [selectedModelId, setSelectedModelIdState] = useState<string>('');
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [executionModes, setExecutionModes] = useState<ResearchExecutionMode[]>([
    'standard',
    'plan',
    'goal',
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setSelectedModelIdState(stored);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoadingModels(true);
    void apiCall({ action: 'list_models' })
      .then(async response => {
        const data = await response.json();
        if (cancelled) return;
        if (!response.ok || !Array.isArray(data.models)) {
          setModels([]);
          return;
        }
        setModels(data.models as PublicResearchModel[]);
        if (Array.isArray(data.executionModes)) {
          const supported = data.executionModes.filter(
            (mode: unknown): mode is ResearchExecutionMode =>
              mode === 'standard' || mode === 'plan' || mode === 'goal'
          );
          if (supported.length > 0) setExecutionModes(supported);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setModels([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingModels(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apiCall]);

  const defaultModel = useMemo(
    () =>
      models.find(model => model.default && model.available) ||
      models.find(model => model.available) ||
      models[0],
    [models]
  );

  useEffect(() => {
    if (models.length === 0) return;
    const selected = models.find(model => model.id === selectedModelId);
    if (selected?.available) return;
    if (defaultModel?.id && defaultModel.id !== selectedModelId) {
      setSelectedModelIdState(defaultModel.id);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, defaultModel.id);
      }
    }
  }, [defaultModel, models, selectedModelId]);

  const setSelectedModelId = useCallback((modelId: string) => {
    setSelectedModelIdState(modelId);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, modelId);
    }
  }, []);

  const selectedModel = useMemo(
    () => models.find(model => model.id === selectedModelId) || defaultModel || null,
    [defaultModel, models, selectedModelId]
  );

  return {
    models,
    selectedModel,
    selectedModelId: selectedModel?.id || selectedModelId,
    setSelectedModelId,
    isLoadingModels,
    executionModes,
  };
}
